import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogTrigger,
} from '@/components/ui-kit/dialog';
import { Button } from '@/components/ui-kit/button';
import { CloudUpload, Loader2, Plus, XCircle } from 'lucide-react';
import { FileUploader, FileInput } from '@/components/file-uploader/file-uploader';
import { useToast } from '@/hooks/use-toast';
import { useGetPreSignedUrlForUpload, useUploadFile } from '@/lib/api/hooks/use-storage-file';
import { isErrorWithErrors } from '@/modules/gpt-chats/utils/error';
import { useAddFiles } from '@/modules/gpt-chats/hooks/use-kb';
import { ModuleName } from '@/constant/modules.constants';
import { useTranslation } from 'react-i18next';

const dropzoneOptions = {
  maxFiles: 5,
  maxSize: 5 * 1024 * 1024,
  multiple: true,
  accept: {
    'application/pdf': ['.pdf'],
    'text/plain': ['.txt'],
  },
};

type AddFileModalProps = {
  folderId: string;
};
export const AddKbFileModal = ({ folderId }: AddFileModalProps) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const { mutateAsync: presignedMutate } = useGetPreSignedUrlForUpload();
  const { mutateAsync: uploadfileMutate } = useUploadFile();
  const { mutateAsync: addFilesMutate } = useAddFiles();

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach(URL.revokeObjectURL);
  }, [files]);

  const removeFile = (idx: number) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const processFile = async (file: File) => {
    try {
      const payload = {
        name: file.name,
        configurationName: 'Default',
        accessModifier: 'Public',
        metaData: '',
        parentDirectoryId: '',
        tags: '',
        moduleName: ModuleName.AICloud,
        projectKey: import.meta.env.VITE_PROJECT_KEY,
      };
      const presignedUrlResponse = await presignedMutate(payload);
      if (!presignedUrlResponse.isSuccess) throw new Error(t('FAILED_TO_GET_UPLOAD_URL'));
      await uploadfileMutate({ url: presignedUrlResponse.uploadUrl, file });
      return presignedUrlResponse.fileId;
    } catch (_error) {
      //
    }
  };

  const uploadFileHandler = async () => {
    setIsUploading(true);
    try {
      const fileIds = (await Promise.all(files.map(processFile))).filter(
        (item) => item != undefined
      );
      if (fileIds?.length < 1) return toast.error({ errors: t('SOMETHING_WENT_WRONG') });
      const res = await addFilesMutate({
        folder_id: folderId,
        file_ids: fileIds,
      });

      if (!res.is_success) return toast.error({ errors: res.error });

      toast.success(`${fileIds.length} ${t('FILES_UPLOADED_SUCCESS')}`);
      setFiles([]);
      setPreviews([]);
      setOpen(false);
    } catch (err: unknown) {
      toast.error({
        errors: isErrorWithErrors(err)
          ? err.errors
          : err instanceof Error
            ? err.message
            : String(err),
      });
    } finally {
      setIsUploading(false);
    }
  };

  const disabled = files.length === 0 || isUploading;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setFiles([]);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline">
          <Plus className="mr-2 h-4 w-4" />
          {t('ADD_FILE')}
        </Button>
      </DialogTrigger>
      <DialogContent className="gap-6" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{t('ADD_FILE')}</DialogTitle>
          <DialogDescription>{t('TRAIN_AGENT_DESCRIPTION')}</DialogDescription>
        </DialogHeader>

        <FileUploader
          value={files}
          onValueChange={(next) => setFiles(next || [])}
          dropzoneOptions={dropzoneOptions}
        >
          <FileInput className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-4">
            <div className="flex w-full items-center justify-center mb-2">
              <CloudUpload className="h-8 w-8 text-low-emphasis" />
            </div>
            <p className="text-sm leading-5">
              <span className="font-semibold text-primary">{t('CLICK_TO_UPLOAD')} </span>
              <span className="font-normal">{t('OR_DRAG_AND_DROP')}</span>
            </p>
            <span className="text-xs text-muted-foreground">{t('UPLOAD_SUPPORT_HINT')}</span>
            <span className="text-xs text-muted-foreground">{t('MAX_FILES_HINT')}</span>
          </FileInput>
        </FileUploader>

        {files.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-4">
            {files.map((f, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="relative h-32 w-32 overflow-hidden rounded border">
                  <button
                    onClick={() => removeFile(i)}
                    className="absolute right-0 top-0 z-10 rounded-full bg-white text-gray-400 shadow hover:text-gray-600"
                  >
                    <XCircle className="h-5 w-5" />
                  </button>
                  <object
                    data={previews[i]}
                    type="application/pdf"
                    className="block h-full w-full"
                  />
                </div>
                <p className="mt-1 w-32 truncate text-center text-xs">{f.name}</p>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="flex flex-col justify-end gap-2 sm:flex-row">
          <DialogClose asChild>
            <Button variant="outline" disabled={isUploading} className="w-full sm:w-20" size="sm">
              {t('CANCEL')}
            </Button>
          </DialogClose>
          <Button
            onClick={uploadFileHandler}
            disabled={disabled}
            className="w-full sm:w-20"
            size="sm"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('ADDING')}
              </>
            ) : (
              t('UPLOAD')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
